import React, { useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import useAddSeats from "api/SubscriptionsResource/useAddSeats";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FiCheckCircle } from "react-icons/fi";

const VoucherPayment = ({ subscriptionId, additionalSeats, totalAmount, onSuccess, currency }) => {
	const voucherInputRef = useRef(null);
	const {
		mutateAsync: addSeats,
		isLoading,
		isSuccess,
		error,
	} = useAddSeats(subscriptionId, onSuccess);

	// Auto-focus the voucher input when component mounts
	useEffect(() => {
		if (voucherInputRef.current) {
			voucherInputRef.current.focus();
		}
	}, []);

	const formik = useFormik({
		initialValues: {
			voucher_number: "",
		},
		validationSchema: Yup.object({
			voucher_number: Yup.string().required("Voucher number is required"),
		}),
		onSubmit: async (values) => {
			try {
				await addSeats({
					subscription_id: subscriptionId,
					additional_seats: additionalSeats,
					payment_method: "Voucher",
					currency: currency,
					total_amount: totalAmount,
					voucher_number: values.voucher_number,
				});

				if (onSuccess) {
					onSuccess();
				}
			} catch (err) {
				// Error is handled by react-query
			}
		},
	});

	if (isSuccess) {
		return (
			<div className="text-center py-8">
				<FiCheckCircle className="mx-auto text-green-500 mb-4" size={64} />
				<h3 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h3>
				<p className="text-gray-600">
					{additionalSeats} seat{additionalSeats > 1 ? "s" : ""} added successfully
				</p>
			</div>
		);
	}

	return (
		<form onSubmit={formik.handleSubmit} className="space-y-4">
			<div>
				<label className="block text-sm font-medium text-gray-700 mb-2">Voucher Number</label>
				<p className="text-sm text-gray-500 mb-3">
					Please enter your voucher code (e.g., TKRpiLHXXy)
				</p>
				<input
					ref={voucherInputRef}
					type="text"
					name="voucher_number"
					value={formik.values.voucher_number}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					placeholder="Enter voucher number"
					className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-akgreen focus:border-transparent"
				/>
				{formik.touched.voucher_number && formik.errors.voucher_number && (
					<p className="mt-1 text-sm text-red-600">{formik.errors.voucher_number}</p>
				)}
				{error && (
					<p className="mt-2 text-sm text-red-600">
						{error?.response?.data?.message || "Payment failed. Please try again."}
					</p>
				)}
			</div>

			<button
				type="submit"
				disabled={isLoading || !formik.isValid}
				className="w-full px-6 py-3 bg-gradient-to-r from-akgreen to-akgreener text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
			>
				{isLoading ? (
					<>
						<AiOutlineLoading3Quarters className="animate-spin" size={20} />
						Processing...
					</>
				) : (
					"Apply Voucher"
				)}
			</button>
		</form>
	);
};

export default VoucherPayment;
